import { useState, useEffect, useRef } from 'react';
import { WifiOff, Wifi } from 'lucide-react';

const GOOGLE_CHECK_URL = 'https://www.google.com/generate_204';
const CHECK_INTERVAL = 5000; // Check every 5 seconds

export default function NetworkStatus() {
  const [isOnline, setIsOnline] = useState(true);
  const [showBackOnline, setShowBackOnline] = useState(false);
  const wasOfflineRef = useRef(false);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        await fetch(GOOGLE_CHECK_URL, {
          method: 'HEAD',
          mode: 'no-cors',
          cache: 'no-store',
        });
        // If fetch succeeds, we're online
        if (!isOnline) {
          setIsOnline(true);
          if (wasOfflineRef.current) {
            setShowBackOnline(true);
            setTimeout(() => setShowBackOnline(false), 3000);
          }
        }
      } catch {
        // If fetch fails, we're offline
        setIsOnline(false);
        wasOfflineRef.current = true;
      }
    };

    // Initial check
    checkConnection();

    // Periodic check
    const interval = setInterval(checkConnection, CHECK_INTERVAL);

    // Also listen to browser events for faster response
    const handleOnline = () => checkConnection();
    const handleOffline = () => {
      setIsOnline(false);
      wasOfflineRef.current = true;
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isOnline]);

  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white py-2 px-4 flex items-center justify-center gap-2 shadow-lg">
        <WifiOff size={18} />
        <span className="text-sm font-medium">No internet connection</span>
      </div>
    );
  }

  if (showBackOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-green-600 text-white py-2 px-4 flex items-center justify-center gap-2 shadow-lg animate-pulse">
        <Wifi size={18} />
        <span className="text-sm font-medium">Back online</span>
      </div>
    );
  }

  return null;
}
